import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { ZakatCalculator } from "@/components/ZakatCalculator";
import { SITE_URL } from "@/lib/seo";
import { buildCalculatorLd } from "@/lib/page-schemas";
import { CalculatorFaqAccordion, RelatedCalculators } from "@/components/calculator/CalculatorEducation";

const TITLE = "Zakat Calculator Pakistan 2026 — Gold, Silver, Cash & Business | DrHint";
const DESCRIPTION =
  "Calculate zakat in Pakistan with today's gold and silver nisab. Add gold, silver, cash, bank balance, shares, prize bonds and business stock, deduct debts, and get your 2.5% zakat.";
const PATH = "/zakat-calculator";

const FAQS = [
  { q: "What is the nisab for zakat in Pakistan today?", a: "Nisab is the value of 612.36 g (52.5 tola) of silver or 87.48 g (7.5 tola) of gold. Most Pakistani scholars and banks use the silver nisab because it is lower. The calculator shows both amounts at today's rate." },
  { q: "Which assets are included in zakat?", a: "Gold, silver, cash at home, bank and mobile-wallet balances, shares and mutual funds, savings certificates, prize bonds, business stock meant for sale, and money others owe you that you expect to get back. Your house, car, furniture and tools you use are not included." },
  { q: "Can I deduct loans before calculating zakat?", a: "Yes. Debts and bills that are due now — such as this month's instalment, unpaid rent or utility bills — are subtracted from your assets. For long-term loans, many scholars allow deducting only the portion due within the coming year." },
  { q: "Is zakat due on gold jewellery worn daily?", a: "In the Hanafi school, followed by most people in Pakistan, zakat is due on all gold and silver jewellery including jewellery worn daily. Other schools exempt jewellery in normal use. If you follow that view, leave worn jewellery out of the gold field." },
  { q: "Does the bank deduct zakat automatically?", a: "Pakistani banks deduct 2.5% on the first day of Ramadan from savings accounts above the nisab set by the government, unless you submit a CZ-50 declaration. If the bank has already deducted zakat, subtract that amount from what this calculator shows." },
];

export const Route = createFileRoute("/zakat-calculator")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE_URL}${PATH}` },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}${PATH}` }],
    scripts: buildCalculatorLd({
      url: `${SITE_URL}${PATH}`,
      name: "Zakat Calculator Pakistan",
      description: DESCRIPTION,
      applicationCategory: "FinanceApplication",
      priceCurrency: "PKR",
      featureList: [
        "Gold (24K/22K/21K/18K) and silver in tola or grams",
        "Cash, bank, shares, prize bonds, business stock and receivables",
        "Debts and bills deducted before zakat",
        "Silver or gold nisab check at today's rate",
      ],
      howTo: {
        name: "How to calculate zakat in Pakistan",
        totalTime: "PT3M",
        steps: [
          { name: "Enter gold and silver", text: "Choose tola or grams, pick your gold karat and enter the total weight of gold and silver you own." },
          { name: "Add cash, savings and business assets", text: "Enter cash at home, bank and wallet balances, shares, prize bonds, business stock and money owed to you." },
          { name: "Deduct debts due now", text: "Enter loans, instalments and bills that are due now. These are subtracted from your assets." },
          { name: "Choose the nisab standard", text: "Select silver nisab (612.36 g) or gold nisab (87.48 g). If net wealth is above nisab, zakat is 2.5% of it." },
        ],
      },
      faqs: FAQS,
      trail: [
        { name: "Home", item: `${SITE_URL}/` },
        { name: "Calculators", item: `${SITE_URL}/calculators` },
        { name: "Zakat Calculator", item: `${SITE_URL}${PATH}` },
      ],
    }),
  }),
  component: ZakatPage,
});

const EXAMPLES = [
  { who: "Salaried family", assets: "3 tola 22K jewellery, Rs 250,000 in bank, Rs 40,000 bill due", result: "Net wealth above silver nisab → 2.5% of (gold value + 250,000 − 40,000)" },
  { who: "Shopkeeper", assets: "Rs 1,200,000 stock, Rs 300,000 cash, Rs 500,000 supplier payment due", result: "Zakat on 1,000,000 → Rs 25,000" },
  { who: "Student", assets: "Rs 60,000 savings, no gold", result: "Below nisab in most years → no zakat due" },
];

function ZakatPage() {
  return (
    <Layout>
      <section className="mx-auto max-w-6xl px-4 pb-2 pt-10">
        <nav aria-label="Breadcrumb" className="mb-3 text-xs text-muted-foreground">
          <Link to="/" className="hover:text-primary">Home</Link> / <Link to="/calculators" className="hover:text-primary">Calculators</Link> / Zakat Calculator
        </nav>
        <h1 className="text-3xl font-black tracking-tight md:text-4xl">Zakat Calculator Pakistan</h1>
        <p className="mt-3 max-w-3xl text-muted-foreground">
          Work out your zakat in three steps: add what you own, subtract what you owe, and compare the result with
          today's nisab. Gold and silver are valued at live Pakistan rates, so the answer matches what a sarafa would pay
          today — not last Ramadan's price. Nothing you type leaves your phone.
        </p>
      </section>

      <ZakatCalculator />

      <article className="mx-auto max-w-4xl space-y-10 px-4 pb-12">
        <section>
          <h2 className="text-xl font-bold">Formula used</h2>
          <div className="mt-3 space-y-2 rounded-2xl border border-border bg-muted/30 p-4 font-mono text-xs md:text-sm">
            <p>Gold value = grams × 24K rate per gram × (karat ÷ 24)</p>
            <p>Net wealth = gold + silver + cash + savings + business stock + receivables − debts due</p>
            <p>If net wealth ≥ nisab → Zakat = net wealth × 2.5%</p>
          </div>
          <p className="mt-3 text-sm text-muted-foreground">
            1 tola = 11.664 grams. Silver nisab = 52.5 tola (612.36 g). Gold nisab = 7.5 tola (87.48 g). Zakat is due once
            your wealth has stayed above nisab for a full lunar (Hijri) year.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold">What to include and what to leave out</h2>
          <div className="mt-3 grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-border bg-card p-4">
              <h3 className="font-semibold">Include</h3>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                <li>All gold and silver (jewellery, coins, bars)</li>
                <li>Cash, bank, JazzCash / Easypaisa balances</li>
                <li>Shares, mutual funds, NSS certificates, prize bonds</li>
                <li>Business stock at sale value</li>
                <li>Plot bought to resell (at market value)</li>
              </ul>
            </div>
            <div className="rounded-2xl border border-border bg-card p-4">
              <h3 className="font-semibold">Leave out</h3>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                <li>Your home, car and household items</li>
                <li>Shop fittings, machinery and tools</li>
                <li>Rented property itself (only saved rent counts)</li>
                <li>Money lent that you don't expect back</li>
                <li>Provident fund you cannot yet withdraw</li>
              </ul>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-bold">Examples</h2>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-muted-foreground">
                  <th className="border-b border-border px-2 py-2">Situation</th>
                  <th className="border-b border-border px-2 py-2">Holdings</th>
                  <th className="border-b border-border px-2 py-2">Outcome</th>
                </tr>
              </thead>
              <tbody>
                {EXAMPLES.map((e) => (
                  <tr key={e.who}>
                    <td className="border-b border-border/60 px-2 py-2 font-medium">{e.who}</td>
                    <td className="border-b border-border/60 px-2 py-2">{e.assets}</td>
                    <td className="border-b border-border/60 px-2 py-2">{e.result}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <CalculatorFaqAccordion faqs={FAQS} />

        <RelatedCalculators
          items={[
            { path: "/pakistan/zakat-nisab", name: "Zakat Nisab Today", description: "Live gold and silver nisab in PKR", icon: "🕌" },
            { path: "/today-gold-rate-calculator-pakistan", name: "Gold Rate Calculator Pakistan", description: "Price by tola, gram and karat", icon: "🥇" },
            { path: "/gold-purity-calculator", name: "Gold Purity Calculator", description: "Convert 22K/21K to pure gold", icon: "💎" },
            { path: "/silver-rate-pakistan", name: "Silver Rate Pakistan", description: "Today's silver per tola", icon: "🥈" },
            { path: "/jewelry-wastage-calculator", name: "Jewellery Wastage Calculator", description: "Making and wastage cost", icon: "💍" },
            { path: "/gold-investment-calculator", name: "Gold Investment Calculator", description: "SIP and past-return estimates", icon: "📈" },
          ]}
        />
      </article>
    </Layout>
  );
}
